import AdminLayout from '@/components/admin/AdminLayout'
import MenuItemForm from '@/components/admin/MenuItemForm'
import MenuItemsList from '@/components/admin/MenuItemsList'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import apiClient from '@/lib/axios'
import { config } from '@/lib/config'
import { useBranches } from '@/queries/useBranches'
import { useMenuItems, type MenuItem } from '@/queries/useMenuItems'
import { useSelectionStore } from '@/store/selectionStore'
import { useAuth, useUser } from '@clerk/clerk-react'
import { Plus, Search } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

export default function MenuItems() {
  const navigate = useNavigate()
  const { getToken } = useAuth()
  const { user, isLoaded: isUserLoaded } = useUser()
  const { selectedBranch, setSelectedBranch, selectedRestaurant } = useSelectionStore()
  const selectedBranchId = selectedBranch?.id
  const selectedRestaurantId = selectedRestaurant?.id
  const { t } = useTranslation('admin')
  const isMongoDBMode = config.useMongoDb
  
  // Detect user role (must be before useBranches to determine if it should be enabled)
  const userRoles = (isUserLoaded && user)
    ? ((user.publicMetadata?.roles as string[] | undefined) || [])
    : []
  const isSuperAdmin = Array.isArray(userRoles) && userRoles.includes('super_admin')
  const isBranchManager = Array.isArray(userRoles) && userRoles.includes('branch_manager')
  
  // Fetch branches unless user is a branch manager (they use assigned branch from Clerk)
  // Even in MongoDB mode, we need branch context for other resources (orders, etc.)
  // Note: Hook must be called unconditionally, but query can be disabled
  const { branches } = useBranches(!isBranchManager && isUserLoaded)
  const hasAccess =
    Array.isArray(userRoles) &&
    (userRoles.includes('restaurant_admin') ||
      userRoles.includes('branch_manager') ||
      userRoles.includes('super_admin'))

  // For branch managers, use assigned branch ID from Clerk metadata
  // Branch context is still needed even in MongoDB mode (for orders, sessions, etc.)
  const assignedBranchId = isBranchManager
    ? (user?.publicMetadata?.assignedBranchId as string | undefined)
    : undefined

  // Use selectedBranchId from store (sidebar context selector manages this)
  // For branch managers, use assigned branch ID from Clerk
  // For super admin, the sidebar context selector will update the store
  // Even in MongoDB mode, keep effectiveBranchId for other resources
  const effectiveBranchId = isBranchManager
    ? assignedBranchId
    : selectedBranchId
  
  // Debug branchId resolution
  console.log('[MenuItems] BranchId resolution:', {
    selectedBranchId,
    selectedRestaurantId,
    effectiveBranchId,
    isSuperAdmin,
    hasBranchId: !!effectiveBranchId,
  })
  
  // Use useMenuItems hook for mutations
  // In MongoDB mode, branchId is undefined; in PostgreSQL mode, use effectiveBranchId
  const {
    createMenuItem,
    updateMenuItem,
    deleteMenuItem,
    toggleAvailability,
    bulkUpdate,
  } = useMenuItems(effectiveBranchId, selectedRestaurantId)

  const [items, setItems] = useState<MenuItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [total, setTotal] = useState(0)

  // Redirect if user doesn't have required role
  useEffect(() => {
    if (isUserLoaded && !hasAccess) {
      navigate('/admin/dashboard', { replace: true })
    }
  }, [isUserLoaded, hasAccess, navigate])

  // Auto-select first branch if only one exists and none is selected (only for non-super-admin, non-branch-manager)
  // Branch context is still needed even in MongoDB mode (for orders, sessions, etc.)
  useEffect(() => {
    if (!isSuperAdmin && !isBranchManager && branches.data && branches.data.length === 1 && !selectedBranchId) {
      const firstBranch = branches.data[0]
      setSelectedBranch({ id: firstBranch.id, name: firstBranch.name })
    }
  }, [branches.data, selectedBranchId, setSelectedBranch, isSuperAdmin, isBranchManager])

  // For branch managers, set the branch ID from Clerk metadata if available
  // Branch context is still needed even in MongoDB mode (for orders, sessions, etc.)
  useEffect(() => {
    if (isBranchManager && assignedBranchId && !selectedBranchId) {
      setSelectedBranch({ id: assignedBranchId, name: '' })
    }
  }, [isBranchManager, assignedBranchId, selectedBranchId, setSelectedBranch])

  // Fetch menu items (manual fetch needed for search/pagination/branch context)
  const fetchMenuItems = useCallback(async () => {
    console.log('[MenuItems] fetchMenuItems called with effectiveBranchId:', effectiveBranchId, 'isMongoDBMode:', isMongoDBMode)

    // In MongoDB mode, we don't need branchId - only restaurantId
    // In PostgreSQL mode, we need both
    if (!isMongoDBMode && !effectiveBranchId) {
      console.error('[MenuItems] BLOCKED: No branchId available - cannot make API call')
      setError('No branch selected. Please select a branch from the sidebar.')
      setLoading(false)
      setItems([])
      setTotal(0)
      return
    }

    if (!selectedRestaurantId) {
      console.error('[MenuItems] BLOCKED: No restaurantId available')
      setError('No restaurant selected.')
      setLoading(false)
      setItems([])
      setTotal(0)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        restaurantId: selectedRestaurantId,
      })

      // Only add branchId in PostgreSQL mode
      if (!isMongoDBMode && effectiveBranchId) {
        params.append('branchId', effectiveBranchId)
      }

      if (searchQuery) {
        params.append('search', searchQuery)
      }

      const token = await getToken()

      // Debug logging
      console.log('[MenuItems] API Request:', {
        isMongoDBMode,
        effectiveBranchId,
        selectedRestaurantId,
        selectedBranchId,
        isSuperAdmin,
        params: params.toString(),
        url: `/menu-items?${params.toString()}`,
        hasBranchId: params.has('branchId'),
        hasRestaurantId: params.has('restaurantId'),
      })

      const response = await apiClient.get(`/menu-items?${params.toString()}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'X-Restaurant-ID': selectedRestaurantId,
          ...(effectiveBranchId && { 'X-Branch-ID': effectiveBranchId }),
        },
      })

      setItems(response.data.data || [])
      setTotal(response.data.meta?.total || 0)
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      setError(error?.response?.data?.message || error?.message || 'Failed to fetch menu items')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [effectiveBranchId, page, limit, searchQuery, getToken, isSuperAdmin, selectedBranchId, selectedRestaurantId, isMongoDBMode])

  useEffect(() => {
    console.log('[MenuItems] useEffect triggered - effectiveBranchId:', effectiveBranchId, 'restaurantId:', selectedRestaurantId, 'isMongoDBMode:', isMongoDBMode)

    // In MongoDB mode, only require restaurantId
    // In PostgreSQL mode, require both branchId and restaurantId
    const canFetch = isMongoDBMode
      ? selectedRestaurantId
      : (effectiveBranchId && selectedRestaurantId)

    if (canFetch) {
      console.log('[MenuItems] Requirements met - calling fetchMenuItems')
      fetchMenuItems()
    } else {
      // If requirements not met, stop loading and show error
      console.log('[MenuItems] Missing required IDs - stopping loading and showing error')
      setLoading(false)
      if (!selectedRestaurantId) {
        setError('No restaurant selected')
      } else if (!isMongoDBMode && !effectiveBranchId) {
        setError('No branch selected')
      }
    }
  }, [effectiveBranchId, selectedRestaurantId, isMongoDBMode, fetchMenuItems])

  const handleCreate = async (data: Partial<MenuItem>) => {
    // In MongoDB mode, branchId is optional
    if (!isMongoDBMode && !effectiveBranchId) {
      throw new Error('No branch selected')
    }
    if (!selectedRestaurantId) {
      throw new Error('No restaurant selected')
    }
    try {
      const token = await getToken()
      // Add restaurant context to request (branchId optional in MongoDB mode)
      const params = new URLSearchParams()
      if (selectedRestaurantId) {
        params.append('restaurantId', selectedRestaurantId)
      }
      if (!isMongoDBMode && effectiveBranchId) {
        params.append('branchId', effectiveBranchId)
      }
      const response = await apiClient.post(`/menu-items?${params.toString()}`, data, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'X-Restaurant-ID': selectedRestaurantId,
          ...(effectiveBranchId && { 'X-Branch-ID': effectiveBranchId }),
        },
      })
      const createdItem = response.data
      setIsCreateOpen(false)
      fetchMenuItems()
      return createdItem
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create menu item'
      throw new Error(errorMessage)
    }
  }

  const handleUpdate = async (id: string, data: Partial<MenuItem>) => {
    // In MongoDB mode, branchId is optional
    if (!isMongoDBMode && !effectiveBranchId) {
      throw new Error('No branch selected')
    }
    if (!selectedRestaurantId) {
      throw new Error('No restaurant selected')
    }
    try {
      // Convert MenuItem to UpdateMenuItemInput (extract category ID if it's an object)
      const updateData: any = { ...data }
      if (updateData.category && typeof updateData.category === 'object') {
        updateData.category = updateData.category._id
      }
      // updateMenuItem.mutateAsync expects (id: string, data: UpdateMenuItemInput)
      await updateMenuItem.mutateAsync(id, updateData)
      fetchMenuItems()
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update menu item'
      throw new Error(errorMessage)
    }
  }

  const handleImageUpload = async (id: string, file: File): Promise<string> => {
    // In MongoDB mode, branchId is optional
    if (!isMongoDBMode && !effectiveBranchId) {
      throw new Error('No branch selected')
    }
    if (!selectedRestaurantId) {
      throw new Error('No restaurant selected')
    }
    try {
      const token = await getToken()
      const formData = new FormData()
      formData.append('file', file)

      const params = new URLSearchParams()
      if (selectedRestaurantId) {
        params.append('restaurantId', selectedRestaurantId)
      }
      if (!isMongoDBMode && effectiveBranchId) {
        params.append('branchId', effectiveBranchId)
      }

      const response = await apiClient.post(`/menu-items/${id}/upload-image?${params.toString()}`, formData, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'X-Restaurant-ID': selectedRestaurantId,
          ...(effectiveBranchId && { 'X-Branch-ID': effectiveBranchId }),
          'Content-Type': 'multipart/form-data',
        },
      })

      const updatedItem = response.data
      fetchMenuItems() // Refresh list to show new image
      return updatedItem.imageUrl || updatedItem.img_url
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to upload image'
      throw new Error(errorMessage)
    }
  }

  const handleToggleAvailability = async (id: string) => {
    try {
      await toggleAvailability.mutateAsync(id)
      fetchMenuItems()
    } catch (err) {
      console.error('Error toggling availability:', err)
      // Error is handled by optimistic update rollback
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteMenuItem.mutateAsync(id)
      fetchMenuItems()
    } catch (err) {
      console.error('Error deleting menu item:', err)
      // Error is handled by optimistic update rollback
    }
  }

  const handleBulkUpdate = async (ids: string[], data: Partial<MenuItem>) => {
    try {
      // Convert MenuItem to UpdateMenuItemInput (extract category ID if it's an object)
      const updateData: any = { ...data }
      if (updateData.category && typeof updateData.category === 'object') {
        updateData.category = updateData.category._id
      }
      await bulkUpdate.mutateAsync({ ids, data: updateData })
      fetchMenuItems()
    } catch (err) {
      console.error('Error bulk updating menu items:', err)
      // Error is handled by optimistic update rollback
    }
  }

  const hasMore = page * limit < total

  // Show loading state while checking user
  if (!isUserLoaded) {
    return (
      <AdminLayout>
        <div className="p-8">Loading...</div>
      </AdminLayout>
    )
  }

  // Show access denied if user doesn't have required role
  if (!hasAccess) {
    return (
      <AdminLayout>
        <div className="p-8">
          <Alert variant="destructive">
            <AlertDescription>
              You do not have permission to access menu items. Only restaurant admins, branch managers, or super admins can access this page.
            </AlertDescription>
          </Alert>
        </div>
      </AdminLayout>
    )
  }

  // Show loading state (wrapped in AdminLayout)
  if (loading && items.length === 0) {
    return (
      <AdminLayout>
        <div className="p-8">Loading menu items...</div>
      </AdminLayout>
    )
  }

  // Check branch loading/availability
  // Even in MongoDB mode, branches are needed for other resources (orders, sessions, etc.)
  // For branch managers, skip branch loading check (they use assigned branch from Clerk)
  // For others, show loading/error states for branches
  if (!isBranchManager) {
    // Show loading while branches are loading
    if (branches.isLoading) {
      return (
        <AdminLayout>
          <div className="p-8">Loading branches...</div>
        </AdminLayout>
      )
    }

    // Show error if no branches available
    if (branches.error || !branches.data || branches.data.length === 0) {
      return (
        <AdminLayout>
          <div className="p-8">
            <Alert variant="destructive">
              <AlertDescription>
                {branches.error
                  ? 'Failed to load branches. Please try again.'
                  : 'No branches found. Please create a branch first.'}
              </AlertDescription>
            </Alert>
          </div>
        </AdminLayout>
      )
    }
  } else {
    // For branch managers, check if assigned branch ID is available
    if (!assignedBranchId) {
      return (
        <AdminLayout>
          <div className="p-8">
            <Alert variant="destructive">
              <AlertDescription>
                No branch assigned. Please contact an administrator to assign you to a branch.
              </AlertDescription>
            </Alert>
          </div>
        </AdminLayout>
      )
    }
  }

  return (
    <AdminLayout>
      <div className="space-y-6" style={{ padding: 'var(--spacing-lg)' }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>{t('menuItems.title')}</h1>
          <p className="text-muted-foreground" style={{ color: 'var(--text-secondary)' }}>{t('menuItems.description')}</p>
        </div>

        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
              {t('menuItems.addItem')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg md:max-w-2xl lg:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('menuItems.createTitle')}</DialogTitle>
              <DialogDescription>
                {t('menuItems.createDescription')}
              </DialogDescription>
            </DialogHeader>
            <MenuItemForm
              onSubmit={async (data) => {
                const createdItem = await handleCreate(data)
                return createdItem
              }}
              onImageUpload={async (id, file) => {
                // For create flow, upload image after item is created
                return handleImageUpload(id, file)
              }}
              isLoading={createMenuItem.isLoading}
              error={createMenuItem.error}
              onCancel={() => setIsCreateOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Branch Selector - Only show for restaurant admins with multiple branches (hidden in MongoDB mode) */}
      {!isMongoDBMode && !isBranchManager && branches.data && branches.data.length > 1 && !isSuperAdmin && (
        <div className="flex items-center gap-4">
          <Label htmlFor="branch-select">Branch:</Label>
          <Select
            value={selectedBranchId || ''}
            onValueChange={(value) => {
              const selectedBranchObj = branches.data?.find(b => b.id === value)
              if (selectedBranchObj) {
                setSelectedBranch({ id: value, name: selectedBranchObj.name })
              }
              setPage(1) // Reset to first page when branch changes
            }}
          >
            <SelectTrigger id="branch-select" className="w-[300px]">
              <SelectValue placeholder="Select a branch" />
            </SelectTrigger>
            <SelectContent>
              {branches.data.map((branch) => (
                <SelectItem key={branch.id} value={branch.id}>
                  {branch.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* MongoDB Mode Indicator */}
      {isMongoDBMode && (
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-sm font-medium text-blue-700">
            📊 MongoDB Mode - Menu items are restaurant-wide (branch selection still needed for orders)
          </span>
        </div>
      )}

      {/* Search Bar */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground rtl:left-auto rtl:right-3" />
          <Input
            placeholder={t('menuItems.searchPlaceholder')}
            className="pl-10 rtl:pl-0 rtl:pr-10"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setPage(1)
            }}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
          {error === 'No branch selected' ? t('menuItems.noBranchSelected') : error}
        </div>
      )}

      {/* Menu Items List */}
      <MenuItemsList
        items={items}
        isLoading={loading}
        onRefresh={() => {
          // In MongoDB mode, only check restaurantId
          // In PostgreSQL mode, check both branchId and restaurantId
          const canRefresh = isMongoDBMode
            ? selectedRestaurantId
            : (effectiveBranchId && selectedRestaurantId)

          if (canRefresh) {
            fetchMenuItems()
          } else {
            console.warn('[MenuItems] onRefresh called but required IDs not available', {
              isMongoDBMode,
              selectedRestaurantId,
              effectiveBranchId,
            })
            if (!selectedRestaurantId) {
              setError('No restaurant selected.')
            } else if (!isMongoDBMode && !effectiveBranchId) {
              setError('No branch selected. Please select a branch first.')
            }
          }
        }}
        onToggleAvailability={handleToggleAvailability}
        onDelete={handleDelete}
        onUpdate={handleUpdate as any}
        onBulkUpdate={handleBulkUpdate as any}
        onImageUpload={handleImageUpload}
      />

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {t('menuItems.showing', {
            from: items.length > 0 ? (page - 1) * limit + 1 : 0,
            to: Math.min(page * limit, total),
            total: total,
          })}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            {t('menuItems.previous')}
          </Button>
          <Button
            variant="outline"
            disabled={!hasMore}
            onClick={() => setPage(page + 1)}
          >
            {t('menuItems.next')}
          </Button>
        </div>
      </div>
      </div>
    </AdminLayout>
  )
}
